import React from 'react';
import ArchitectureLayout from 'components/architecture/ArchitectureLayout';

/**
 * Bot Operations API Endpoint Documentation
 */
const BotOperations = () => {
  const sections = [
    {
      id: 'overview',
      title: 'Bot Operations API',
      content: `The Bot Operations API provides endpoints for managing bot conversations, sessions, and responses.

**Base Path**: \`/api/v1/chatbot\`

**Authentication**: API key or public access (varies by endpoint)

**Rate Limiting**: 1000 requests per minute per API key`
    },
    {
      id: 'endpoints',
      title: 'Available Endpoints',
      content: `### Create Session
**Path**: \`/api/v1/chatbot/sessions\`
**Method**: \`POST\`

Creates a new conversation session with a bot.

**Request Body**:
\`\`\`json
{
  "resource_id": "bot-resource-id",
  "user_id": "optional-user-id",
  "metadata": {}
}
\`\`\`

**Response**: Session object with session ID

### Generate Response
**Path**: \`/api/v1/chatbot/generate\`
**Method**: \`POST\`

Sends a message to the bot and generates a response. Supports both synchronous and asynchronous modes.

**Request Body**:
\`\`\`json
{
  "session_id": "session-id",
  "message": "User message",
  "async": false
}
\`\`\`

**Response**: Bot response or queue ID for async operations

### Get Response Status
**Path**: \`/api/v1/chatbot/sessions/{session_id}/status\`
**Method**: \`GET\`

Retrieves the status and result of an asynchronous response generation.

**Path Parameter**: \`queue_id\` - Queue ID from async response

**Response**: Response status and content

### Download Conversation
**Path**: \`/api/v1/chatbot/download-conversation\`
**Method**: \`POST\`

Downloads the full conversation history for a session.

### Delete Conversation
**Path**: \`/api/v1/chatbot/delete-conversation\`
**Method**: \`POST\`

Deletes a conversation session and all associated data.

### Summarize Conversation
**Path**: \`/api/v1/chatbot/summarize-conversation\`
**Method**: \`POST\`

Generates a summary of the conversation.

### Get Public Config
**Path**: \`/api/v1/chatbot/config\`
**Method**: \`GET\`

Retrieves public configuration for a bot (no authentication required).`
    },
    {
      id: 'examples',
      title: 'Example Requests',
      content: `### Create Session
\`\`\`bash
curl -X POST "https://api.example.com/api/v1/chatbot/create-session" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "resource_id": "bot-123",
    "user_id": "user-456"
  }'
\`\`\`

### Generate Response
\`\`\`bash
curl -X POST "https://api.example.com/api/v1/chatbot/generate-response" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "session_id": "session-789",
    "message": "Hello, how can you help me?",
    "async": false
  }'
\`\`\``
    },
    {
      id: 'api-docs',
      title: 'Interactive Documentation',
      content: `For interactive API documentation with try-it-out functionality, visit:

[Bot Operations API in API Documentation →](/api-docs#/v1/chatbot)

You can test all endpoints, view request/response schemas, and see authentication requirements.`
    }
  ];

  return (
    <ArchitectureLayout
      title="Bot Operations API"
      lastUpdated="2025-01-15"
      sections={sections}
    />
  );
};

export default BotOperations;

